import React, { useCallback, useMemo, useState } from 'react'
import { useTypedTranslation } from 'src/shared/i18next/hooks/use-typed-translation'
import { components, OptionProps, SingleValueProps } from 'react-select'
import { CheckIcon } from 'src/editor/shared/icons/check-icon'
import { Trash02Icon } from 'src/editor/shared/icons/trash-02-icon'
import { Loader } from 'src/editor/shared/ui-lib/loader'
import {
  OptionType,
  SelectNew,
} from 'src/editor/shared/ui-lib/select-new/select-new'
import {
  WebsiteThemeListColorsInterface,
  WebsiteThemeListInterface,
} from 'src/shared/types/website-settings-types'
import { twMerge } from 'tailwind-merge'
import { ColorsPreview } from './colors-preview'

interface ThemeSelectProps {
  themes?: WebsiteThemeListInterface[]
  activeThemeId: WebsiteThemeListInterface['id'] | null
  update: (value: WebsiteThemeListInterface['id'] | null) => void
  onDelete: (value: WebsiteThemeListInterface['id']) => void
  isLoading: boolean
}

interface ThemeSelectOptionInterface
  extends OptionType<WebsiteThemeListInterface['id'] | null> {
  colors?: WebsiteThemeListColorsInterface
  isCommon?: boolean
  onDelete?: (id: WebsiteThemeListInterface['id']) => void
}

export const ThemeSelect = ({
  themes = [],
  activeThemeId,
  update,
  onDelete,
  isLoading,
}: ThemeSelectProps) => {
  const { t } = useTypedTranslation()
  const options = useMemo<ThemeSelectOptionInterface[]>(() => {
    const themeOptions = themes.map(theme => ({
      value: theme.id,
      label: theme.name,
      colors: theme.colors,
      isCommon: theme.isCommon,
      onDelete: onDelete,
    }))

    return [
      ...themeOptions,
      {
        value: null,
        label: t('website_settings.sidebar.theme.add_custom_theme'),
      },
    ]
  }, [themes, onDelete])

  const selectedOption =
    options.find(option => option.value === activeThemeId) || null

  const handleChange = useCallback(
    (value: ThemeSelectOptionInterface | null) => {
      if (value && value.value !== activeThemeId) {
        update(value.value)
      }
    },
    [update],
  )

  return (
    <SelectNew<number | null, false, ThemeSelectOptionInterface>
      isMulti={false}
      isClearable={false}
      selectedOption={selectedOption}
      onChange={handleChange}
      options={options}
      components={{ Option, SingleValue }}
      isLoading={isLoading}
    />
  )
}

const SingleValue = (
  props: SingleValueProps<ThemeSelectOptionInterface, false>,
) => {
  const { data } = props

  return (
    <components.SingleValue
      {...props}
      className={twMerge(props.className, 'flex')}
    >
      <div>{data.label}</div>
      {data.colors && <ColorsPreview colors={data.colors} className="ml-3" />}
    </components.SingleValue>
  )
}

const Option = (props: OptionProps<ThemeSelectOptionInterface, false>) => {
  const { isSelected, data } = props
  const [isDeleting, setIsDeleting] = useState(false)

  return (
    <components.Option
      {...props}
      className={twMerge(
        'hover:!bg-bgSecondary !cursor-pointer relative px-3 py-2 transition-colors',
        isSelected ? '!bg-bgSecondary' : '!bg-bgPrimaryBase',
        isDeleting &&
          'hover:!bg-bgPrimaryBase !bg-bgSecondary/50 !cursor-default pointer-events-none',
      )}
    >
      {isDeleting && (
        <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
          <Loader className="h-6 w-6" />
        </div>
      )}
      <div
        className={twMerge(
          'flex flex-col gap-1 text-textPrimary',
          isSelected && 'cursor-default bg-bgSecondary',
        )}
      >
        <div className="flex items-center justify-between">
          <span className="text-M text-textPrimary">{data.label}</span>
          {isSelected && (
            <CheckIcon
              width={16}
              height={16}
              className="mr-1 ml-1.5 fill-textPrimary"
            />
          )}
        </div>
        {data.value && (
          <div className="flex h-6 items-center justify-between">
            {data.colors && <ColorsPreview colors={data.colors} />}

            {!data.isCommon && data.onDelete && (
              <button
                type="button"
                disabled={isSelected}
                className={twMerge(
                  'flex h-6 w-6 cursor-pointer items-center justify-center text-iconTertiary transition-colors hover:text-iconError',
                  isSelected &&
                    'cursor-default disabled:hover:text-iconTertiary',
                )}
                onClick={async e => {
                  e.stopPropagation()
                  if (data.onDelete) {
                    setIsDeleting(true)
                    await data.onDelete(
                      data.value as WebsiteThemeListInterface['id'],
                    )
                    setIsDeleting(false)
                  }
                }}
              >
                <Trash02Icon width={16} height={16} />
              </button>
            )}
          </div>
        )}
      </div>
    </components.Option>
  )
}
